<script setup lang="ts">
import HrefCreateButton from '@/components/Buttons/HrefCreateButton.vue'
import FullTableLayout from '@/components/Layouts/FullTableLayout.vue'
import StackLayout from '@/components/Layouts/StackLayout.vue'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import BaseTable from '@/components/Table/BaseTable.vue'
import TableActionColumn from '@/components/Table/TableActionColumn.vue'
import TableColumn from '@/components/Table/TableColumn.vue'
import TablePagination from '@/components/Table/TablePagination.vue'
import TypographyItem from '@/components/Typography/TypographyItem.vue'
import { ADMIN_DEVICE_TEMPLATE_KEYS_ROUTES } from '@/router/routing/adminRouting/adminRoutes'
import store from '@/store'
import { EDeviceConfigActions } from '@/store/deviceConfigStore/DeviceConfigStoreTypes'
import { EStoreModules } from '@/store/storeType'
import { ESortBy, ESortByAlphabetical, ESortByDate, getSortByKey } from '@/types/enum/SortByEnum'
import { type TemplateKeysFilters } from '@/types/filters/TemplateFilters'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { PermissionsEnums } from '@/types/permissions/PermissionsEnum'
import { booleanToString } from '@/utils/dataUtils'
import { isCurrentPageOverTotal } from '@/utils/paginationUtils'
import { addToQuery, getQueryFilters, getQuerySort } from '@/utils/queryUtils'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { localFallback } from '../../../../locales/langUtils'
import { EPropertyType, EPropertyTypeLabels } from '../../../../types/enum/PropertyTypeEnum'
import DeviceTemplateKeysFilters from './components/DeviceTemplateKeysFilters.vue'

type Props = {
    isCurrentTab: boolean
    permission?: PermissionsEnums
    createButtonHref: string
    editButtonHref: string
}
const props = defineProps<Props>()

const router = useRouter()
const { t } = useI18n()
const i18n = useI18n()
const data = computed(() => store.state.deviceConfig.templateKeys)
const filters = ref<TemplateKeysFilters>({
    ...getQueryFilters(),
    property_type: EPropertyType.CONFIG
})
const sort = ref<ESortBy>(getQuerySort() ?? ESortBy.ALPHABETICAL_ASC)

const columns = computed(() => ({
    key: { field: 'key', label: t('deviceConfig.templateKeys.table.key') },
    description: {
        field: 'description_trans',
        label: t('deviceConfig.templateKeys.table.description')
    },
    propertyType: {
        field: 'property_type',
        label: t('deviceConfig.templateKeys.table.propertyType')
    },
    isAdmin: { field: 'is_admin', label: t('deviceConfig.templateKeys.table.isAdmin') }
}))

const getDeviceConfigs = () =>
    store.dispatch(
        `${EStoreModules.DEVICE_CONFIG}/${EDeviceConfigActions.FETCH_DEVICE_TEMPLATE_KEYS}`,
        {
            filters: filters.value,
            sort: getSortByKey(sort.value),
            per_page: data.value.per_page,
            page: props.isCurrentTab ? data.value.current_page : 1
        }
    )

const goToCreate = () => router.push(ADMIN_DEVICE_TEMPLATE_KEYS_ROUTES.createUrl)

const handleChangeFilters = (value: TemplateKeysFilters) => {
    filters.value = { ...filters.value, ...value }
    data.value.current_page = 1
    getDeviceConfigs()
}
const handleChangePageSize = (value: number) => {
    data.value.per_page = value
    data.value.current_page = 1
    getDeviceConfigs()
}
const handleChangePageNumber = (pageNumber: number) => {
    data.value.current_page = pageNumber
    getDeviceConfigs()
}
const handleChangeSortBy = (sortBy: ESortBy) => {
    sort.value = sortBy
    getDeviceConfigs()
}

const deleteDeviceConfig = (device: DeviceModel) => {
    store
        .dispatch(
            `${EStoreModules.DEVICE_CONFIG}/${EDeviceConfigActions.DELETE_DEVICE_TEMPLATE_KEY}`,
            {
                id: device.id,
                name: device.name
            }
        )
        .then(() =>
            isCurrentPageOverTotal(
                data.value.total - 1,
                data.value.per_page,
                data.value.current_page,
                data.value.last_page
            )
                ? handleChangePageNumber(data.value.current_page - 1)
                : getDeviceConfigs()
        )
}

const table = ref()

watch(
    () => props.isCurrentTab,
    isCurrent => (isCurrent ? addToQuery({ page: data.value.current_page }, true) : null)
)
</script>

<template>
    <StackLayout direction="column" :gap="8">
        <PermissionWrapper :permission="permission">
            <HrefCreateButton
                :href="createButtonHref"
                :label="$t('deviceConfig.templateKeys.buttons.createKey')"
                @click="goToCreate" />
        </PermissionWrapper>

        <FullTableLayout
            isResponsiveSmallScreen
            :orderOptions="[...ESortByAlphabetical, ...ESortByDate]"
            :sortBy="sort"
            @changeSortBy="handleChangeSortBy">
            <template #filters>
                <DeviceTemplateKeysFilters :filters="filters" @change="handleChangeFilters" />
            </template>

            <template #table>
                <BaseTable v-bind:ref="table" :data="data.data" :loading="data.isLoading">
                    <template #columns>
                        <TableColumn field="keyName" :label="columns['key'].label" />
                        <TableColumn
                            :field="columns['description'].field"
                            :label="columns['description'].label"
                            v-slot="props">
                            <TypographyItem
                                :label="
                                    props.row.description
                                        ? localFallback(props.row.description)
                                        : ''
                                " />
                        </TableColumn>
                        <TableColumn :label="columns['propertyType'].label" v-slot="props">
                            <TypographyItem
                                :label="
                                    $t(
                                        EPropertyTypeLabels[
                                            props.row.property_type as EPropertyType
                                        ]
                                    )
                                " />
                        </TableColumn>
                        <TableColumn :label="columns['isAdmin'].label" v-slot="props">
                            <TypographyItem :label="booleanToString(props.row.is_admin)" />
                        </TableColumn>
                        <TableActionColumn
                            @delete="deleteDeviceConfig"
                            :editPermission="permission"
                            :deletePermission="permission"
                            :editButtonHref="editButtonHref" />
                    </template>
                </BaseTable>
                <TablePagination
                    :perPage="data.per_page"
                    :currentPage="data.current_page"
                    :lastPage="data.last_page"
                    :total="data.total"
                    forcePropsCurrentPage
                    @change:pageSize="handleChangePageSize"
                    @change:pageNumber="handleChangePageNumber" />
            </template>

            <template #dialogContent>
                <slot name="dialogContent" />
            </template>
        </FullTableLayout>
    </StackLayout>
</template>
